/**
 * Utility functions for segment tracking and calculations
 */
import { Segment, SegmentsByLap } from '../types/segment';

/**
 * Create a new segment object with all necessary properties
 */
interface CreateSegmentParams {
  segmentNumber: number;
  lapNumber: number;
  distanceMeters: number;
  currentSegmentTime: number;
  totalSegmentTime: number;
  segmentPace: number | null;
}

export const createSegmentObject = ({
  segmentNumber,
  lapNumber,
  distanceMeters,
  currentSegmentTime,
  totalSegmentTime,
  segmentPace,
}: CreateSegmentParams): Segment => {
  return {
    id: `segment-${segmentNumber}-${Date.now()}`,
    lapNumber,
    distance: distanceMeters,
    time: currentSegmentTime,
    pace: segmentPace,
    timestamp: Date.now(),
  };
};

/**
 * Update segments by lap structure with a new segment
 */
export const updateSegmentsByLap = (
  prevSegmentsByLap: SegmentsByLap,
  newSegment: Segment
): SegmentsByLap => {
  const updatedSegmentsByLap = { ...prevSegmentsByLap };

  const lapNumberToUse = newSegment.lapNumber;
  if (!updatedSegmentsByLap[lapNumberToUse]) {
    updatedSegmentsByLap[lapNumberToUse] = [];
  }

  updatedSegmentsByLap[lapNumberToUse].push(newSegment);
  return updatedSegmentsByLap;
};

/**
 * Filter segments based on lap view settings
 */
interface GetFilteredSegmentsParams {
  showAllLaps: boolean;
  allSegments: Segment[];
  segmentsByLap: SegmentsByLap;
  currentLapNumber: number;
}

export const getFilteredSegments = ({
  showAllLaps,
  allSegments,
  segmentsByLap,
  currentLapNumber,
}: GetFilteredSegmentsParams): Segment[] => {
  if (showAllLaps) {
    return allSegments;
  } else {
    // If only showing current lap segments, filter by current lap number
    if (segmentsByLap && segmentsByLap[currentLapNumber]) {
      return segmentsByLap[currentLapNumber];
    }
    return allSegments.filter(segment => segment.lapNumber === currentLapNumber);
  }
};
